<template>
<div>
    <h2><i class="fa fa-fw fa-lg fa-home"></i> {{ $label('common.address') }}</h2>
    <p></p>

    <div class="v-form-partial">
        <div class="v-form">
            <fieldset>
                <div class="row">
                    <!-- BEGIN Country -->
                    <section class="col col-6">
                        <label class="label">{{ $label('common.country') }}</label>

                        <label class="select">
                            <select v-model="address.country_id" :disabled="updating">
                                <option v-for="country in countries" :value="country.id" :selected="countryShouldBeSelected(country)" v-bind:key="country.id">{{ $label('common.country_' + country.slug) }}</option>
                            </select>
                            <i></i>
                        </label>

                        <p v-if="hasError('country_id')" class="text-danger">{{ getError('country_id') }}</p>
                    </section>
                    <!-- END Country -->

                    <!-- BEGIN State -->
                    <section class="col col-6">
                        <label class="label">{{ $label('common.state') }}</label>

                        <label class="select">
                            <select v-model="address.state_id" :disabled="updating">
                                <option v-for="state in states" :value="state.id" :selected="stateShouldBeSelected(state)" v-bind:key="state.id">{{ state.name }}</option>
                            </select>
                            <i></i>
                        </label>

                        <p v-if="hasError('state_id')" class="text-danger">{{ getError('state_id') }}</p>
                    </section>
                    <!-- END State -->
                </div>

                <div class="row">
                    <!-- BEGIN City -->
                    <section class="col col-8">
                        <label class="label">{{ $label('common.city') }}</label>

                        <label class="input">
                            <input v-model="address.city"
                                v-on:keyup.enter="requestModelChange"
                                :class="{ 'text-input-error': hasError('city') }"
                                :placeholder="$label('common.city')"
                                :disabled="updating"
                                type="text"
                            />
                        </label>

                        <p v-if="hasError('city')" class="text-danger">{{ getError('city') }}</p>
                    </section>
                    <!-- END City -->

                    <!-- BEGIN Post code -->
                    <section class="col col-4">
                        <label class="label">{{ $label('common.post_code_short') }}</label>

                        <label class="input">
                            <input v-model="address.post_code"
                                v-on:keyup.enter="requestModelChange"
                                :class="{ 'text-input-error': hasError('post_code') }"
                                :placeholder="$label('common.post_code_short')"
                                :disabled="updating"
                                type="text"
                            />
                        </label>

                        <p v-if="hasError('post_code') && !updating" class="text-danger">{{ getError('post_code') }}</p>
                    </section>
                    <!-- END Post code -->
                </div>

                <div class="row">
                    <!-- BEGIN Steet name -->
                    <section class="col col-8">
                        <label class="label">{{ $label('common.street_name_short') }}</label>

                        <label class="input">
                            <input v-model="address.street_name"
                                v-on:keyup.enter="requestModelChange"
                                :class="{ 'text-input-error': hasError('street_name') }"
                                :placeholder="$label('common.street_name_long')"
                                :disabled="updating"
                                type="text"
                            />
                        </label>

                        <p v-if="hasError('street_name')" class="text-danger">{{ getError('street_name') }}</p>
                    </section>
                    <!-- END Steert name -->

                    <!-- BEGIN Steert number -->
                    <section class="col col-4">
                        <label class="label">{{ $label('common.street_number') }}</label>

                        <label class="input">
                            <input v-model="address.street_number"
                                v-on:keyup.enter="requestModelChange"
                                :class="{ 'text-input-error': hasError('street_number') }"
                                :placeholder="$label('common.street_number')"
                                :disabled="updating"
                                type="text"
                            />
                        </label>

                        <p v-if="hasError('street_number')" class="text-danger">{{ getError('street_number') }}</p>
                    </section>
                    <!-- END Street number -->
                </div>

                <div class="row">
                    <!-- BEGIN additional information -->
                    <section class="col col-12">
                        <label class="label">{{ $label('common.additional_address_info_short') }}</label>

                        <label class="textarea">
                            <textarea v-model="address.additional_information"
                                :class="{ 'text-input-error': hasError('additional_information') }"
                                :placeholder="$label('common.additional_address_info_long')"
                                :disabled="updating"
                                rows="5">
                            </textarea>
                        </label>

                        <p v-if="hasError('additional_information')" class="text-danger">{{ getError('additional_information') }}</p>
                    </section>
                    <!-- END Additional information -->
                </div>
            </fieldset>

        </div>
    </div>
</div>
</template>

<script>
import Api from '@/api';
import FormErrorsMixin from '@/mixins/FormErrors.js';
import ModelActionsMixin from '@/mixins/ModelActions.js';

export default {
    props: ['operatorId', 'updating'],

    mixins: [FormErrorsMixin, ModelActionsMixin],

    data() {
        return {
            validatedInputs: [
                'country_id', 'state_id', 'city',
                'post_code', 'street_name', 'street_number',
                'additional_information',
            ],
            countries: [],
            states: [],
            parentView: 'operatorBaseDetails',
            modelData: 'address',
            address: {
                id: null,
                country_id: null,
                state_id: null,
                city: null,
                post_code: null,
                street_name: null,
                street_number: null,
                additional_information: null,
            },
        }
    },

    mounted() {
        this.initializeOperatorAddress();
    },

    methods: {
        initializeOperatorAddress() {
            if (!this.operatorId) {
                return;
            }
            this.resetAddress();

            this.getCountries(() => {
                this.getAddress();
            });
        },

        getCountries(successCallback) {
            Api().get('countries')
                .then((response) => {
                    this.countries = response.data.data;

                    if (typeof successCallback == 'function') {
                        successCallback();
                    }
                })
                .catch((error) => {
                    //
                });
        },

        getStates() {
            Api().get('countries/' + this.selectedCountryId + '/states')
                .then((response) => {
                    this.states = response.data.data;
                })
                .catch((error) => {
                    //
                });
        },

        getAddress() {
            let endpoint = this.$partnerEndpoint('operators/' + this.operatorId + '/address');
            Api().get(endpoint)
                .then((response) => {
                    this.address = response.data.data;
                    this.emitModelLoadedEvent();
                })
                .catch((error) => {
                    this.emitModelLoadedEvent(false);
                });
        },

        storeModel() {
            this.resetErrors();
            let endpoint = this.$partnerEndpoint('operators/' + this.operatorId + '/address');

            Api().post(endpoint, this.address)
                .then((response) => {
                    this.emitModelUpdatedEvent();
                })
                .catch((error) => {
                    this.emitModelUpdatedEvent(false);
                    this.storeErrors(error.response.data.errors);
                });
        },

        updateModel() {
            this.resetErrors();
            let endpoint = this.$partnerEndpoint('addresses/' + this.address.id);

            Api().put(endpoint, this.address)
                .then((response) => {
                    this.emitModelUpdatedEvent();
                })
                .catch((error) => {
                    this.emitModelUpdatedEvent(false);
                    this.storeErrors(error.response.data.errors);
                });
        },

        /**
         * Delete the operator address.
         */
        deleteModel() {
            this.resetErrors();
            let endpoint = this.$partnerEndpoint('addresses/' + this.address.id);

            Api().delete(endpoint)
                .then(() => {
                    this.emitModelUpdatedEvent();
                })
                .catch(() => {
                    this.emitModelUpdatedEvent(false);
                });
        },

        /**
         * Reset the loaded address.
         */
        resetAddress() {
            for (var i = 0; i < this.validatedInputs.length; i++) {
                let index = this.validatedInputs[i];
                this.address[index] = null;
            }
        },

        /**
         * Returns true if given country should be selected, false otherwise.
         */
        countryShouldBeSelected(country) {
            return this.address.country_id == country.id;
        },

        /**
         * Returns true if given state sould be selected, false otherwise.
         */
        stateShouldBeSelected(state) {
            return this.address.state_id == state.id;
        },
    },

    computed: {
        selectedCountryId() {
            return this.address.country_id;
        },
    },

    watch: {
        /**
         * Reload the states each time the selected country is changed.
         */
        selectedCountryId(newSelectedCountryId) {
            if (newSelectedCountryId) {
                this.getStates();
            }
        },

        /**
         * Reload the address every time the operator id changes.
         */
        operatorId(operatorId) {
            if (operatorId) {
                this.initializeOperatorAddress();
            }
        },
    },
}
</script>
